import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Star, ThumbUp, ThumbDown } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import apiService from '../services/apiService';

const RatingFeedbackPage = () => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [tabValue, setTabValue] = useState(0); // 0 = All, 1 = Driver, 2 = Passenger

  useEffect(() => {
    fetchRatingFeedbackOptions();
  }, []);

  const fetchRatingFeedbackOptions = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getRatingFeedbackOptions();

      if (response.success) {
        setOptions(response.data);
      } else {
        setError(response.message || 'Failed to load rating feedback options');
      }
    } catch (err) {
      console.error('Rating feedback options error:', err);
      setError('Failed to load rating feedback options');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditMode(false);
    setSelectedOption(null);
    setOpenDialog(true);
  };

  const handleEdit = (option) => {
    setEditMode(true);
    setSelectedOption(option);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this feedback option?')) {
      return;
    }

    try {
      const response = await apiService.deleteRatingFeedbackOption(id);

      if (response.success) {
        alert('Feedback option deleted successfully');
        fetchRatingFeedbackOptions();
      } else {
        alert(response.message || 'Failed to delete feedback option');
      }
    } catch (err) {
      alert('Failed to delete feedback option');
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      let response;
      if (editMode) {
        response = await apiService.updateRatingFeedbackOption(selectedOption.id, values);
      } else {
        response = await apiService.addRatingFeedbackOption(values);
      }

      if (response.success) {
        alert(`Feedback option ${editMode ? 'updated' : 'added'} successfully`);
        setOpenDialog(false);
        fetchRatingFeedbackOptions();
      } else {
        alert(response.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Rating feedback option operation error:', err);
      alert('Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    type: Yup.string().oneOf(['driver', 'passenger']).required('Type is required'),
    rating_type: Yup.string().oneOf(['positive', 'negative']).required('Rating type is required'),
  });

  const getFilteredOptions = () => {
    if (tabValue === 0) return options;
    if (tabValue === 1) return options.filter((o) => o.type === 'driver');
    if (tabValue === 2) return options.filter((o) => o.type === 'passenger');
    return options;
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'title',
      headerName: 'Feedback Option',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Star color="warning" fontSize="small" />
          <Typography variant="body2" fontWeight={500}>
            {params.row.title}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'For Rating',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.row.type === 'driver' ? 'Driver' : 'Passenger'}
          color={params.row.type === 'driver' ? 'primary' : 'secondary'}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'rating_type',
      headerName: 'Rating Type',
      width: 150,
      renderCell: (params) => (
        <Chip
          icon={params.row.rating_type === 'positive' ? <ThumbUp fontSize="small" /> : <ThumbDown fontSize="small" />}
          label={params.row.rating_type === 'positive' ? 'Positive (4-5)' : 'Negative (1-3)'}
          color={params.row.rating_type === 'positive' ? 'success' : 'error'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status === 'active' ? 'Active' : 'Inactive'}
          color={params.row.status === 'active' ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton size="small" onClick={() => handleEdit(params.row)} color="primary">
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Manage Rating Feedback
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage feedback options shown when rating drivers and passengers
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Add Feedback Option
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`All (${options.length})`} />
            <Tab label={`For Rating Driver (${options.filter((o) => o.type === 'driver').length})`} />
            <Tab label={`For Rating Passenger (${options.filter((o) => o.type === 'passenger').length})`} />
          </Tabs>

          <DataGrid
            rows={getFilteredOptions()}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Feedback Option Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Feedback Option</DialogTitle>
        <Formik
          initialValues={{
            title: selectedOption?.title || '',
            type: selectedOption?.type || '',
            rating_type: selectedOption?.rating_type || '',
            status: selectedOption?.status || 'active',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    name="title"
                    label="Feedback Option Title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                    placeholder="e.g., Friendly, Clean Car, Safe Driving"
                  />

                  <FormControl fullWidth error={touched.type && Boolean(errors.type)}>
                    <InputLabel>For Rating (Who is being rated)</InputLabel>
                    <Select
                      name="type"
                      value={values.type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="For Rating (Who is being rated)"
                    >
                      <MenuItem value="driver">Driver (Shown to Passengers)</MenuItem>
                      <MenuItem value="passenger">Passenger (Shown to Drivers)</MenuItem>
                    </Select>
                    {touched.type && errors.type && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.type}
                      </Typography>
                    )}
                  </FormControl>

                  <FormControl fullWidth error={touched.rating_type && Boolean(errors.rating_type)}>
                    <InputLabel>Rating Type</InputLabel>
                    <Select
                      name="rating_type"
                      value={values.rating_type}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Rating Type"
                    >
                      <MenuItem value="positive">Positive (Shown for 4-5 stars)</MenuItem>
                      <MenuItem value="negative">Negative (Shown for 1-3 stars)</MenuItem>
                    </Select>
                    {touched.rating_type && errors.rating_type && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                        {errors.rating_type}
                      </Typography>
                    )}
                  </FormControl>

                  {editMode && (
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="Status"
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    bgcolor: '#FFC107',
                    color: '#000',
                    '&:hover': {
                      bgcolor: '#FFD54F',
                    },
                  }}
                >
                  {editMode ? 'Update' : 'Save'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default RatingFeedbackPage;
